import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditArticleDialog } from "@/components/EditArticleDialog";
import { AddArticleDialog } from "@/components/AddArticleDialog";
import { useArticles } from "@/hooks/useArticles";
import { FAQManager } from "@/components/FAQManager";
import { useAdminAuth } from "@/components/AdminAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useChateaux } from "@/hooks/useChateaux";
import { ChateauFormDialog } from "@/components/ChateauFormDialog";
import { useSumos, Sumo } from "@/hooks/useSumos";
import { SumoEditor } from "@/components/SumoEditor";
import { SumoFormDialog } from "@/components/SumoFormDialog";
import { ReservationsManager } from "@/components/admin/ReservationsManager";
import { ClosurePeriodsManager } from "@/components/admin/ClosurePeriodsManager";

import { MultiImageUpload } from "@/components/MultiImageUpload";
import { ImageGallery } from "@/components/ImageGallery";
import { 
  Settings, 
  FileText, 
  Castle, 
  Image, 
  Video, 
  Plus, 
  Edit, 
  Trash2,
  Eye,
  Lock,
  X,
  BarChart3,
  Zap,
  CalendarCheck,
  Ban,
  Loader2
} from "lucide-react";
import { AnalyticsDashboard } from "./AnalyticsDashboard";

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminPanel = ({ isOpen, onClose }: AdminPanelProps) => {
  const [editArticleOpen, setEditArticleOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [addArticleOpen, setAddArticleOpen] = useState(false);
  const [selectedChateau, setSelectedChateau] = useState<string>("");
  const { isAdminLoggedIn, loginAdmin, isLoading } = useAdminAuth();
  const { toast } = useToast();
  const { chateaux, fetchChateaux, createChateau, updateChateau, deleteChateau, toggleVisibility } = useChateaux();
  const { articles, fetchArticles, deleteArticle, togglePublished } = useArticles();
  const { sumos, fetchSumos, createSumo, updateSumo, deleteSumo } = useSumos();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Wrapper functions pour adapter les signatures
  const handleCreateChateau = async (chateauData: any) => {
    await createChateau(chateauData);
  };

  const handleUpdateChateau = async (chateauData: any) => {
    const { id, ...updates } = chateauData;
    await updateChateau(id, updates);
  };

  // Charger tous les châteaux, articles et sumos (visibles et cachés) pour l'admin
  useEffect(() => {
    if (isAdminLoggedIn && isOpen) {
      console.log('📋 Admin Panel: Chargement des données...');
      fetchChateaux(true); // includeHidden = true pour l'admin
      fetchArticles(true); // includeUnpublished = true pour l'admin
      fetchSumos(true); // includeHidden = true pour l'admin
    }
  }, [isAdminLoggedIn, isOpen]);

  // Gestion de la touche Escape pour fermer la popup
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleLogin = async () => {
    const cleanEmail = email.trim();

    if (!cleanEmail || !password) {
      setAuthError("Entrez votre email et votre mot de passe");
      return;
    }

    const success = await loginAdmin(cleanEmail, password);
    if (success) {
      setAuthError("");
    } else {
      setAuthError("Email ou mot de passe incorrect");
    }
  };

  const handleForgotPassword = async () => {
    const cleanEmail = email.trim();

    if (!cleanEmail) {
      setAuthError("Entrez votre email avant de demander un nouveau mot de passe");
      return;
    }

    setIsResettingPassword(true);
    setAuthError("");

    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      redirectTo: 'https://castel.lovable.app/reset-password'
    });

    setIsResettingPassword(false);

    if (error) {
      setAuthError(error.message);
      return;
    }

    toast({
      title: "Email envoyé",
      description: "Cliquez sur le lien reçu puis choisissez votre nouveau mot de passe."
    });
  };


  if (!isOpen) return null;

  // Login Form - Centré au milieu de la page
  if (!isAdminLoggedIn) {
    return (
      <>
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 cursor-pointer" 
          onClick={onClose}
          aria-label="Fermer la popup en cliquant à l'extérieur"
        />
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 min-h-screen pointer-events-none">
          <div className="bg-background rounded-lg shadow-2xl w-full max-w-md relative mx-auto border pointer-events-auto">
            {/* Header avec X */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-primary text-white rounded-t-lg">
              <div className="flex items-center">
                <Lock className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-display font-bold">
                  Connexion Admin
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="text-white hover:text-gray-300 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Login Form */}
            <div className="p-6 space-y-4 text-foreground">
              <div>
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="lechateaudeclementine@gmail.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-foreground">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  autoComplete="current-password"
                />
              </div>
              {authError && (
                <p className="text-red-500 text-sm">{authError}</p>
              )}
              <Button onClick={handleLogin} className="w-full" disabled={!email.trim() || !password}>
                Se connecter
              </Button>
              <Button
                type="button"
                variant="link"
                className="w-full"
                onClick={handleForgotPassword}
                disabled={isResettingPassword}
              >
                {isResettingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi du lien...
                  </>
                ) : (
                  'Mot de passe oublié ?'
                )}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main Admin Panel (authenticated)

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 cursor-pointer" 
        onClick={onClose}
        aria-label="Fermer la popup en cliquant à l'extérieur"
      />
      <div className="fixed inset-4 bg-background rounded-lg shadow-2xl z-50 overflow-hidden pointer-events-auto">
        <div className="flex flex-col h-full">
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b bg-gradient-primary text-white">
            <div className="flex items-center">
              <Settings className="w-6 h-6 mr-3" />
              <h2 className="text-2xl font-display font-bold">Panneau Administrateur</h2>
            </div>
            <Button variant="outline" onClick={onClose} className="text-white border-white hover:bg-white/20">
              Fermer
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 text-foreground">
            <Tabs defaultValue="reservations" className="w-full">
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="reservations">
                  <CalendarCheck className="w-4 h-4 mr-2" />
                  Réservations
                </TabsTrigger>
                <TabsTrigger value="analytics">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="articles">
                  <FileText className="w-4 h-4 mr-2" />
                  Articles
                </TabsTrigger>
                <TabsTrigger value="produits">
                  <Castle className="w-4 h-4 mr-2" />
                  Produits
                </TabsTrigger>
                <TabsTrigger value="fermetures">
                  <Ban className="w-4 h-4 mr-2" />
                  Fermetures
                </TabsTrigger>
                <TabsTrigger value="faq">
                  <FileText className="w-4 h-4 mr-2" />
                  FAQ
                </TabsTrigger>
                <TabsTrigger value="media">
                  <Image className="w-4 h-4 mr-2" />
                  Médias
                </TabsTrigger>
              </TabsList>

              {/* Reservations Tab */}
              <TabsContent value="reservations" className="space-y-6">
                <ReservationsManager />
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <AnalyticsDashboard />
              </TabsContent>

              {/* Articles Tab */}
              <TabsContent value="articles" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-display font-semibold">Gestion des Articles</h3>
                  <Button onClick={() => setAddArticleOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un Article
                  </Button>
                </div>
                
                <Card>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground mb-4">
                      Gérez vos articles de blog, créez du contenu optimisé SEO, et suivez les performances.
                    </p>
                    <div className="grid gap-4">
                      {articles.map((article) => (
                        <div key={article.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{article.title}</h4>
                              <Badge variant={article.is_published ? "default" : "secondary"}>
                                {article.is_published ? "Publié" : "Brouillon"}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {new Date(article.created_at).toLocaleDateString('fr-FR')} • {article.read_time} min de lecture
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedArticle(article);
                                setEditArticleOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => togglePublished(article.id, !article.is_published)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => {
                                if (confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
                                  deleteArticle(article.id);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {articles.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          Aucun article pour le moment. Commencez par en créer un !
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

          {/* Produits Tab - Châteaux + Sumos */}
          <TabsContent value="produits" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-display font-semibold">Tous les Produits</h3>
              <div className="flex gap-2">
                <ChateauFormDialog
                  onSave={handleCreateChateau}
                  trigger={
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau Château
                    </Button>
                  }
                  title="Ajouter un Nouveau Château"
                />
                <SumoFormDialog
                  onSave={async (sumoData) => {
                    await createSumo(sumoData as Omit<Sumo, 'id' | 'created_at' | 'updated_at'>);
                  }}
                  trigger={
                    <Button variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau Sumo
                    </Button>
                  }
                  title="Ajouter un Nouveau Sumo"
                />
              </div>
            </div>

            {/* Liste unifiée des produits */}
            <Card>
              <CardHeader>
                <CardTitle>🏰 Châteaux Gonflables ({chateaux.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {chateaux.map((chateau) => (
                    <div key={chateau.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{chateau.nom}</h4>
                          <Badge variant={chateau.is_visible ? "default" : "secondary"}>
                            {chateau.is_visible ? "Visible" : "Masqué"}
                          </Badge>
                          {chateau.quantite > 1 && (
                            <Badge variant="outline">Stock: {chateau.quantite}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {chateau.prix} • {chateau.taille} • {chateau.capacite} • {chateau.age_recommande}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <ChateauFormDialog
                          chateau={chateau}
                          onSave={handleUpdateChateau}
                          trigger={
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          }
                          title={`Modifier ${chateau.nom}`}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => toggleVisibility(chateau.id, !chateau.is_visible)}
                        >
                          {chateau.is_visible ? <Eye className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => {
                            if (confirm(`Supprimer "${chateau.nom}" ?`)) {
                              deleteChateau(chateau.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {chateaux.length === 0 && (
                    <p className="text-center py-4 text-muted-foreground">Aucun château</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>🥋 Sumos Gonflables ({sumos.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {sumos.map((sumo) => (
                    <div key={sumo.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{sumo.title}</h4>
                          <Badge variant={sumo.is_visible ? "default" : "secondary"}>
                            {sumo.is_visible ? "Visible" : "Masqué"}
                          </Badge>
                          {sumo.quantite > 1 && (
                            <Badge variant="outline">Stock: {sumo.quantite}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {sumo.price} • {sumo.features.slice(0, 3).join(', ')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <SumoFormDialog
                          sumo={sumo}
                          onSave={async (sumoData) => {
                            await updateSumo(sumo.id, sumoData);
                          }}
                          trigger={
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          }
                          title={`Modifier ${sumo.title}`}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => updateSumo(sumo.id, { is_visible: !sumo.is_visible })}
                        >
                          {sumo.is_visible ? <Eye className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => {
                            if (confirm(`Supprimer "${sumo.title}" ?`)) {
                              deleteSumo(sumo.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {sumos.length === 0 && (
                    <p className="text-center py-4 text-muted-foreground">Aucun sumo</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

              {/* Fermetures Tab */}
              <TabsContent value="fermetures" className="space-y-6">
                <ClosurePeriodsManager />
              </TabsContent>

              {/* FAQ Tab */}
              <TabsContent value="faq" className="space-y-6">
                <FAQManager />
              </TabsContent>

              {/* Médias Tab */}
              <TabsContent value="media" className="space-y-6">
                <h3 className="text-xl font-display font-semibold">Gestion des Médias</h3>
                <div className="grid grid-cols-1 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Image className="w-5 h-5 mr-2" />
                        Upload Multiple d'Images
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Château concerné</Label>
                        <select 
                          className="w-full px-3 py-2 border border-border rounded-md"
                          value={selectedChateau}
                          onChange={(e) => setSelectedChateau(e.target.value)}
                        >
                          <option value="">Sélectionner un château...</option>
                          {chateaux.map((chateau) => (
                            <option key={chateau.id} value={chateau.id}>
                              {chateau.nom}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedChateau && (
                        <>
                          <MultiImageUpload 
                            chateauId={selectedChateau}
                            onImagesUploaded={(imageUrls) => console.log('Images uploadées:', imageUrls)}
                            maxFiles={10}
                          />
                          <div className="mt-6">
                            <ImageGallery 
                              chateauId={selectedChateau}
                              onImageDeleted={() => console.log('Image supprimée')}
                            />
                          </div>
                        </>
                      )}
                      {!selectedChateau && (
                        <p className="text-sm text-muted-foreground">
                          Sélectionnez d'abord un château pour gérer ses images.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <AddArticleDialog 
        open={addArticleOpen} 
        onOpenChange={setAddArticleOpen}
      />
      
      <EditArticleDialog
        open={editArticleOpen}
        onOpenChange={setEditArticleOpen}
        article={selectedArticle}
      />
    </>
  );
};
